<script setup lang="ts">
import VersionChecker from "@/components/shared/VersionChecker.vue";
import { useAuthStore } from "@/stores/authStore";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter, type RouteMeta } from "vue-router";
import { computed } from "vue";
import { ERROR_ACTION_LABELS, type TErrorAction } from "@/router/errors";
import { useLandingRoute, type TReachableSection } from "@/composables/shared/useLandingRoute";
import PrimaryButton from "@/components/shared/buttons/PrimaryButton.vue";
import TertiaryButton from "@/components/shared/buttons/TertiaryButton.vue";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const { reachableSections } = useLandingRoute();

const meta = route.meta as RouteMeta & {
  content: {
    code: number;
    title: string;
    text: string;
    subText: string;
    action?: TErrorAction;
  };
};

const action = computed((): TErrorAction => meta.content?.action ?? "back");

// Only worth offering to someone with an ability to read them from. An anonymous
// visitor on a 404 gets the primary action alone, as before.
const sections = computed(() => (authStore.isLoggedIn ? reachableSections.value : []));

function handleAction() {
  if (action.value === "logout") authStore.userLogout();
  else if (action.value === "home") router.push("/");
  else router.back();
}

// The shared buttons emit `click` rather than taking `:to`, so navigate here
// instead of widening their props for this one caller.
function goToSection(section: TReachableSection) {
  router.push(section.route);
}
</script>

<template>
  <!-- The ground is always the dark brand blue, so the content is pinned to the light
       theme: in dark mode `primary` is a PALE tint, which left the white copy (and the
       surface-derived buttons) unreadable. Forcing the palette here keeps one legible
       treatment in both themes without hardcoding hex values. -->
  <v-theme-provider
    theme="orbisLightTheme"
    class="tw:!h-dvh"
  >
    <v-layout class="tw:!h-dvh tw:!bg-primary">
      <v-main>
        <VersionChecker no-offset />
        <v-container
          class="tw:!max-w-[1366px] tw:h-dvh tw:flex tw:items-center tw:justify-center tw:text-center"
        >
          <div class="tw:text-white tw:text-center">
            <div class="tw:text-5xl tw:md:text-8xl tw:font-bold">{{ meta.content.code }}</div>
            <div class="tw:text-2xl tw:md:text-4xl tw:font-bold">{{ t(meta.content.title) }}</div>
            <div class="tw:text-lg tw:md:text-2xl tw:font-normal tw:!mt-4 tw:md:!mt-8">
              {{ t(meta.content.text) }}
            </div>
            <div class="tw:text-md tw:opacity-50">
              {{ t(meta.content.subText) }}
            </div>
            <primary-button
              class="tw:!mt-8"
              @click="handleAction"
            >
              {{ t(ERROR_ACTION_LABELS[action]) }}
            </primary-button>

            <template v-if="sections.length">
              <div class="tw:!mt-10 tw:text-sm tw:opacity-50">
                {{ t("generic.pageError.availableSections") }}
              </div>
              <div class="tw:!mt-4 tw:flex tw:flex-wrap tw:justify-center tw:gap-3">
                <tertiary-button
                  v-for="section in sections"
                  :key="section.label"
                  size="default"
                  @click="goToSection(section)"
                >
                  <v-icon
                    :icon="section.icon"
                    size="16"
                  />
                  {{ t(section.label) }}
                </tertiary-button>
              </div>
            </template>
          </div>
        </v-container>
      </v-main>
    </v-layout>
  </v-theme-provider>
</template>

<style scoped></style>
